import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Button,
  TextField,
  Tabs,
  Tab,
  Chip,
  IconButton,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  Tooltip,
  Fab,
  LinearProgress,
  Autocomplete,
  FormControlLabel,
  Switch,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  useTheme,
  useMediaQuery,
  Divider,
} from '@mui/material';
import {
  Image as ImageIcon,
  Palette as PaletteIcon,
  VideoLibrary as AnimateIcon,
  ThreeSixty as View360Icon,
  Collections as CollectionIcon,
  FaceRetouchingNatural as VirtualTryOnIcon,
  Add as AddIcon,
  Upload as UploadIcon,
  AutoAwesome as MagicIcon,
  Save as SaveIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandIcon,
  CheckCircle as CheckIcon,
  Checkroom as CheckroomIcon,
  History as HistoryIcon,
  ContentCopy as CopyIcon,
  Description as BriefIcon,
  Tune as ConfigIcon,
} from '@mui/icons-material';
import {
  FashionDesignSession,
  FashionDesign,
  DesignPhase,
  FashionCategory,
  FashionStyle,
  ImageModel,
  FASHION_PROMPT_TEMPLATES,
  VirtualTryOnModel,
  GarmentCategory,
  TryOnMode,
  GarmentPhotoType,
  VIRTUAL_TRYON_MODELS,
} from '../models/fashionDesignStudio';
import {
  MultiFashionRequest,
  FashionSession,
  SEASONS,
  TARGET_MARKETS,
  PRICE_POINTS,
  THEME_SUGGESTIONS,
  COLOR_SUGGESTIONS,
} from '../models/fashion';
import fashionDesignStudioService from '../services/fashionDesignStudioService';
import { FashionService } from '../services/fashionService';
import { useAssetRetrieval } from '../hooks/useAssetRetrieval';
import AssetRetrievalService, { GeneratedAsset } from '../services/assetRetrievalService';
import { useAuthStore } from '../stores/AuthStore';
import ModelSelector from './common/ModelSelector';
import videoGenerationService, { FASHION_VIDEO_MODELS } from '../services/videoGenerationService';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ p: { xs: 2, sm: 3 } }}>{children}</Box>}
    </div>
  );
};

export const FashionDesignStudio: React.FC = () => {
  // Responsive hooks
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  // Auth store for user ID
  const { user } = useAuthStore();

  // Fetch persisted assets from backend (asset-based agent)
  const {
    assets: backendAssets,
    loading: backendLoading,
    error: backendError,
    refetch: refetchBackendData
  } = useAssetRetrieval({
    agentType: 'FashionDesignStudio',
    sessionBased: false,  // Asset-based agent
    autoFetch: true,
  });

  // Session state
  const [session, setSession] = useState<FashionDesignSession | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Current design state
  const [currentDesign, setCurrentDesign] = useState<FashionDesign | null>(null);
  const [designs, setDesigns] = useState<FashionDesign[]>([]);

  // Multi-agent concept generation state (from FashionMultiAgent)
  const [designBrief, setDesignBrief] = useState('');
  const [targetMarket, setTargetMarket] = useState('');
  const [season, setSeason] = useState('');
  const [pricePoint, setPricePoint] = useState('');
  const [themeElements, setThemeElements] = useState<string[]>([]);
  const [colorPalette, setColorPalette] = useState<string[]>([]);
  const [fabricPreferences, setFabricPreferences] = useState('');
  const [includeSustainability, setIncludeSustainability] = useState(false);
  const [iterations, setIterations] = useState(3);
  const [maxTokens, setMaxTokens] = useState(2048);
  const [trendAnalysis, setTrendAnalysis] = useState('');
  const [designConcept, setDesignConcept] = useState('');
  const [technicalSpecs, setTechnicalSpecs] = useState('');

  // Existing session selection state
  const [useExistingConcept, setUseExistingConcept] = useState(false);
  const [fashionSessions, setFashionSessions] = useState<FashionSession[]>([]);
  const [selectedSessionId, setSelectedSessionId] = useState<string>('');
  const [loadingFashionSessions, setLoadingFashionSessions] = useState(false);

  // Track the Fashion Design Agent sessionId to use for image generation
  const [fashionConceptSessionId, setFashionConceptSessionId] = useState<string | null>(null);

  // State for fetched images from existing concept
  const [loadingConceptImages, setLoadingConceptImages] = useState(false);

  // State for fetched videos from existing concept
  const [animationVideos, setAnimationVideos] = useState<string[]>([]);
  const [videos360, setVideos360] = useState<string[]>([]);
  const [loadingConceptVideos, setLoadingConceptVideos] = useState(false);

  // State for fetched virtual try-on images from existing concept
  const [virtualTryOnImages, setVirtualTryOnImages] = useState<string[]>([]);
  const [loadingConceptTryOns, setLoadingConceptTryOns] = useState(false);
  // LLM model selection for concept generation (following established pattern)
  const [selectedLlmModel, setSelectedLlmModel] = useState<string>(
    localStorage.getItem('fashionLlmModel') ||
    localStorage.getItem('selectedModel') ||
    ''
  );

  // Image generation state
  const [prompt, setPrompt] = useState('');
  const [model, setModel] = useState<ImageModel>('flux-krea'); // Default to text-to-image model
  const [category, setCategory] = useState<FashionCategory>('ready-to-wear');
  const [style, setStyle] = useState<FashionStyle>('editorial');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [generatedImages, setGeneratedImages] = useState<string[]>([]);
  const [generating, setGenerating] = useState(false);

  // Settings
  const [numImages, setNumImages] = useState(4);
  const [guidanceScale, setGuidanceScale] = useState(7.5);
  const [improvePrompts, setImprovePrompts] = useState(true);
  const [seed, setSeed] = useState<number>(() => Math.floor(Math.random() * 2147483647)); // Random seed by default

  // Context & Styling settings
  const [contextStrength, setContextStrength] = useState(0.85);
  const [preserveStructure, setPreserveStructure] = useState(true);

  // Video generation state
  const [videoModel, setVideoModel] = useState<string>(FASHION_VIDEO_MODELS.HAILUO_STANDARD);
  const [videoDuration, setVideoDuration] = useState(5);
  const [videoPrompt, setVideoPrompt] = useState('');
  const [generatingVideo, setGeneratingVideo] = useState(false);
  const [generatedVideoUrl, setGeneratedVideoUrl] = useState<string | null>(null);
  const [selectedImageForVideo, setSelectedImageForVideo] = useState<string | null>(null);
  const [videoResponse, setVideoResponse] = useState<any>(null);

  // Virtual Try-On state
  const [tryOnModel, setTryOnModel] = useState<VirtualTryOnModel>('fashn-v16');
  const [humanImageFile, setHumanImageFile] = useState<File | null>(null);
  const [humanImagePreview, setHumanImagePreview] = useState<string | null>(null);
  const [garmentImageFile, setGarmentImageFile] = useState<File | null>(null);
  const [garmentImagePreview, setGarmentImagePreview] = useState<string | null>(null);
  const [garmentDescription, setGarmentDescription] = useState('');

  // FASHN parameters
  const [tryOnCategory, setTryOnCategory] = useState<GarmentCategory>('auto');
  const [tryOnMode, setTryOnMode] = useState<TryOnMode>('balanced');
  const [garmentPhotoType, setGarmentPhotoType] = useState<GarmentPhotoType>('auto');

  // CAT-VTON parameters
  const [catGarmentType, setCatGarmentType] = useState<'upper' | 'lower' | 'overall' | 'inner' | 'outer'>('upper');

  // Leffa parameters
  const [leffaGarmentType, setLeffaGarmentType] = useState<'upper_body' | 'lower_body' | 'dresses'>('upper_body');
  const [enableSafetyChecker, setEnableSafetyChecker] = useState(true);

  // Common parameters for CAT-VTON and Leffa
  const [tryOnGuidanceScale, setTryOnGuidanceScale] = useState(2.5);
  const [tryOnInferenceSteps, setTryOnInferenceSteps] = useState(30);

  // Results
  const [generatingTryOn, setGeneratingTryOn] = useState(false);
  const [tryOnResults, setTryOnResults] = useState<string[]>([]);
  const [tryOnMask, setTryOnMask] = useState<string | null>(null);
  const [hasNsfwConcepts, setHasNsfwConcepts] = useState<boolean | undefined>(undefined);

  // Load backend assets for display
  useEffect(() => {
    if (backendAssets && backendAssets.length > 0) {
      // Convert backend assets to FashionDesign format
      const convertedDesigns: FashionDesign[] = backendAssets.map(asset => {
        try {
          const design = JSON.parse(asset.textContent || '{}');
          return {
            ...design,
            id: asset.id,
            createdAt: asset.createdAt,
          };
        } catch {
          return null;
        }
      }).filter(Boolean) as FashionDesign[];

      // Merge with existing designs
      setDesigns(prevDesigns => {
        const designMap = new Map();
        prevDesigns.forEach(d => designMap.set(d.id, d));
        convertedDesigns.forEach(d => designMap.set(d.id, d));
        return Array.from(designMap.values());
      });
    }
  }, [backendAssets]);

  useEffect(() => {
    initializeSession();
  }, []);

  // Load Fashion Multi-Agent sessions from backend
  useEffect(() => {
    const loadFashionSessions = async () => {
      if (!user?.id) {
        console.log('[FashionDesignStudio] No user ID available, skipping session load');
        setLoadingFashionSessions(false);
        return;
      }

      setLoadingFashionSessions(true);
      try {
        console.log('[FashionDesignStudio] Loading fashion sessions for user:', user.id);

        // Use dedicated fashion designs sessions endpoint
        const response = await AssetRetrievalService.getFashionDesignSessions(user.id, {
          limit: 50
        });

        console.log('[FashionDesignStudio] Fashion sessions response:', response);

        // Map backend response to FashionSession format
        const sessions: FashionSession[] = (response.sessions || []).map((session: any) => ({
          id: session.sessionId,
          sessionId: session.sessionId,
          userId: session.userId,
          status: session.status,
          designBrief: session.designBrief || '',
          targetMarket: session.targetMarket,
          season: session.season,
          pricePoint: session.pricePoint,
          themeElements: session.themeElements || [],
          colorPalette: session.colorPalette || [],
          fabricPreferences: session.fabricPreferences,
          trendAnalysis: session.trendAnalysis || '',
          designConcept: session.designConcept || '',
          technicalSpecifications: session.technicalSpecifications || '',
          createdAt: new Date(session.createdAt),
          lastUpdatedAt: session.lastUpdatedAt ? new Date(session.lastUpdatedAt) : undefined,
          expiresAt: session.expiresAt ? new Date(session.expiresAt) : undefined,
          includeSustainability: session.includeSustainability,
          iterations: session.iterations,
          assetIds: session.assetIds,
          assetCount: session.assetCount,
          assets: session.assets
        }));

        console.log('[FashionDesignStudio] Mapped sessions:', sessions.length, sessions);
        setFashionSessions(sessions);
      } catch (err) {
        console.error('[FashionDesignStudio] Failed to load fashion sessions:', err);
      } finally {
        setLoadingFashionSessions(false);
      }
    };

    loadFashionSessions();
  }, [user?.id]);

  const initializeSession = async () => {
    setLoading(true);
    try {
      // Try to load existing session from localStorage
      const savedSessionId = localStorage.getItem('fashionStudioSessionId');
      if (savedSessionId) {
        const loadedSession = await fashionDesignStudioService.loadSession({ sessionId: savedSessionId });
        if (loadedSession) {
          setSession(loadedSession);
          setDesigns(loadedSession.designs);
        } else {
          // Session not found, create new one
          const newSession = await fashionDesignStudioService.createSession(
            `Fashion Session ${new Date().toLocaleDateString()}`,
            category
          );
          setSession(newSession);
          localStorage.setItem('fashionStudioSessionId', newSession.sessionId);
        }
      } else {
        // Create new session
        const newSession = await fashionDesignStudioService.createSession(
          `Fashion Session ${new Date().toLocaleDateString()}`,
          category
        );
        setSession(newSession);
        localStorage.setItem('fashionStudioSessionId', newSession.sessionId);
      }
    } catch (err: any) {
      setError('Failed to initialize session: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Handler for selecting an existing Fashion Multi-Agent session
  const handleSelectExistingSession = async (sessionId: string) => {
    setSelectedSessionId(sessionId);
    const selectedSession = fashionSessions.find(s => s.id === sessionId);

    if (selectedSession) {
      // Set the Fashion Design Agent sessionId to use for image generation
      setFashionConceptSessionId(selectedSession.id);

      // Populate all fields from the selected session
      setDesignBrief(selectedSession.designBrief);
      setTargetMarket(selectedSession.targetMarket || '');
      setSeason(selectedSession.season || '');
      setPricePoint(selectedSession.pricePoint || '');
      setThemeElements(selectedSession.themeElements || []);
      setColorPalette(selectedSession.colorPalette || []);
      setFabricPreferences(selectedSession.fabricPreferences || '');
      setIncludeSustainability(selectedSession.includeSustainability || false);

      // Populate generated content
      setTrendAnalysis(selectedSession.trendAnalysis || '');
      setDesignConcept(selectedSession.designConcept || '');
      setTechnicalSpecs(selectedSession.technicalSpecifications || '');

      // Auto-populate the Generate tab prompt field with the design concept
      // This allows the user to skip concept generation and go straight to image generation
      if (selectedSession.designConcept) {
        setPrompt(selectedSession.designConcept);
      } else if (selectedSession.designBrief) {
        // Fallback to design brief if no design concept available
        setPrompt(selectedSession.designBrief);
      }

      // Fetch existing assets for this Fashion Design Agent session
      await fetchConceptImages(selectedSession.id);
      await fetchAnimationVideos(selectedSession.id);
      await fetch360Videos(selectedSession.id);
      await fetchVirtualTryOnImages(selectedSession.id);

      // Optionally switch to the Config or Generate tab so user can start generating
      // setActiveTab(1); // Uncomment to auto-switch to Config tab
      // setActiveTab(2); // Uncomment to auto-switch to Generate tab
    }
  };

  // Fetch images associated with a Fashion Design Agent session
  const fetchConceptImages = async (conceptSessionId: string) => {
    if (!user?.id) return;

    setLoadingConceptImages(true);
    try {
      console.log('[FashionDesignStudio] Fetching assets for session:', conceptSessionId);

      // Use session-based endpoint to get all assets for this session
      const sessionAssets = await AssetRetrievalService.getSessionAssets(conceptSessionId);

      console.log('[FashionDesignStudio] Total session assets:', sessionAssets.length);

      // Filter for ImageGeneration assets only
      const imageAssets = sessionAssets.filter(asset => asset.agentType === 'ImageGeneration');

      console.log('[FashionDesignStudio] ImageGeneration assets for session:', imageAssets.length);

      // Convert assets to image URLs
      const imageUrls = imageAssets
        .map(asset => {
          // Extract image URL from asset - prioritize assetUrl
          let imageUrl = asset.assetUrl;

          // Fallback to other fields if assetUrl is not available
          if (!imageUrl) {
            try {
              const metadata = asset.metadataJson ? JSON.parse(asset.metadataJson) : {};
              imageUrl = metadata.url || metadata.imageUrl || asset.storageUrl || asset.mediaUrl;
            } catch (error) {
              console.warn('[FashionDesignStudio] Failed to parse asset metadata:', error);
            }
          }

          return imageUrl;
        })
        .filter(url => url !== null && url !== undefined) as string[];

      console.log('[FashionDesignStudio] Extracted image URLs:', imageUrls.length);

      // Set the generated images
      setGeneratedImages(imageUrls);

    } catch (error) {
      console.error('[FashionDesignStudio] Failed to fetch concept images:', error);
    } finally {
      setLoadingConceptImages(false);
    }
  };

  // Fetch animation videos associated with a Fashion Design Agent session
  const fetchAnimationVideos = async (conceptSessionId: string) => {
    if (!user?.id) return;

    setLoadingConceptVideos(true);
    try {
      console.log('[FashionDesignStudio] Fetching animation videos for session:', conceptSessionId);

      // Use session-based endpoint to get all assets for this session
      const sessionAssets = await AssetRetrievalService.getSessionAssets(conceptSessionId);

      console.log('[FashionDesignStudio] Total session assets:', sessionAssets.length);
      console.log('[FashionDesignStudio] All session assets:', sessionAssets);

      // Filter for VideoGeneration assets with subType='animation'
      // Check both exact match and case-insensitive match
      const animationAssets = sessionAssets.filter(
        asset => asset.agentType === 'VideoGeneration' &&
        (asset.subType === 'animation' || asset.subType?.toLowerCase() === 'animation')
      );

      console.log('[FashionDesignStudio] Animation videos for session:', animationAssets.length);
      console.log('[FashionDesignStudio] Animation video assets:', animationAssets);

      // Convert assets to video URLs
      const videoUrls = animationAssets
        .map(asset => {
          // Extract video URL from asset - prioritize assetUrl
          let videoUrl = asset.assetUrl;

          // Fallback to other fields if assetUrl is not available
          if (!videoUrl) {
            try {
              const metadata = asset.metadataJson ? JSON.parse(asset.metadataJson) : {};
              videoUrl = metadata.url || metadata.videoUrl || asset.storageUrl || asset.mediaUrl;
            } catch (error) {
              console.warn('[FashionDesignStudio] Failed to parse asset metadata:', error);
            }
          }

          return videoUrl;
        })
        .filter(url => url !== null && url !== undefined) as string[];

      console.log('[FashionDesignStudio] Extracted animation video URLs:', videoUrls.length);

      // Set the animation videos
      setAnimationVideos(videoUrls);

    } catch (error) {
      console.error('[FashionDesignStudio] Failed to fetch animation videos:', error);
    } finally {
      setLoadingConceptVideos(false);
    }
  };

  // Fetch 360 videos associated with a Fashion Design Agent session
  const fetch360Videos = async (conceptSessionId: string) => {
    if (!user?.id) return;

    setLoadingConceptVideos(true);
    try {
      console.log('[FashionDesignStudio] Fetching 360 videos for session:', conceptSessionId);

      // Use session-based endpoint to get all assets for this session
      const sessionAssets = await AssetRetrievalService.getSessionAssets(conceptSessionId);

      console.log('[FashionDesignStudio] Total session assets for 360:', sessionAssets.length);

      // Filter for VideoGeneration assets with subType='360'
      // Check both exact match and case-insensitive match
      const video360Assets = sessionAssets.filter(
        asset => asset.agentType === 'VideoGeneration' &&
        (asset.subType === '360' || asset.subType?.toLowerCase() === '360')
      );

      console.log('[FashionDesignStudio] 360 videos for session:', video360Assets.length);
      console.log('[FashionDesignStudio] 360 video assets:', video360Assets);

      // Convert assets to video URLs
      const videoUrls = video360Assets
        .map(asset => {
          // Extract video URL from asset - prioritize assetUrl
          let videoUrl = asset.assetUrl;

          // Fallback to other fields if assetUrl is not available
          if (!videoUrl) {
            try {
              const metadata = asset.metadataJson ? JSON.parse(asset.metadataJson) : {};
              videoUrl = metadata.url || metadata.videoUrl || asset.storageUrl || asset.mediaUrl;
            } catch (error) {
              console.warn('[FashionDesignStudio] Failed to parse asset metadata:', error);
            }
          }

          return videoUrl;
        })
        .filter(url => url !== null && url !== undefined) as string[];

      console.log('[FashionDesignStudio] Extracted 360 video URLs:', videoUrls.length);

      // Set the 360 videos
      setVideos360(videoUrls);

    } catch (error) {
      console.error('[FashionDesignStudio] Failed to fetch 360 videos:', error);
    } finally {
      setLoadingConceptVideos(false);
    }
  };

  // Fetch virtual try-on images associated with a Fashion Design Agent session
  const fetchVirtualTryOnImages = async (conceptSessionId: string) => {
    if (!user?.id) return;

    setLoadingConceptTryOns(true);
    try {
      console.log('[FashionDesignStudio] Fetching virtual try-on images for session:', conceptSessionId);

      // Use session-based endpoint to get all assets for this session
      const sessionAssets = await AssetRetrievalService.getSessionAssets(conceptSessionId);

      console.log('[FashionDesignStudio] Total session assets for try-on:', sessionAssets.length);

      // Filter for VirtualTryOn assets
      const tryOnAssets = sessionAssets.filter(
        asset => asset.agentType === 'VirtualTryOn'
      );

      console.log('[FashionDesignStudio] Virtual try-on images for session:', tryOnAssets.length);
      console.log('[FashionDesignStudio] Virtual try-on assets:', tryOnAssets);

      // Convert assets to image URLs
      const imageUrls = tryOnAssets
        .map(asset => {
          // Extract image URL from asset - prioritize assetUrl
          let imageUrl = asset.assetUrl;

          // Fallback to other fields if assetUrl is not available
          if (!imageUrl) {
            try {
              const metadata = asset.metadataJson ? JSON.parse(asset.metadataJson) : {};
              imageUrl = metadata.url || metadata.imageUrl || asset.storageUrl || asset.mediaUrl;
            } catch (error) {
              console.warn('[FashionDesignStudio] Failed to parse asset metadata:', error);
            }
          }

          return imageUrl;
        })
        .filter(url => url !== null && url !== undefined) as string[];

      console.log('[FashionDesignStudio] Extracted virtual try-on image URLs:', imageUrls.length);

      // Set the virtual try-on images
      setVirtualTryOnImages(imageUrls);

    } catch (error) {
      console.error('[FashionDesignStudio] Failed to fetch virtual try-on images:', error);
    } finally {
      setLoadingConceptTryOns(false);
    }
  };

  const handleCreateDesign = async () => {
    if (!session) return;

    setLoading(true);
    try {
      const design = await fashionDesignStudioService.addDesign(
        session,
        `Design ${designs.length + 1}`,
        prompt || 'Untitled design',
        category
      );
      setDesigns([...designs, design]);
      setCurrentDesign(design);
    } catch (err: any) {
      setError('Failed to create design: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleTextToImage = async () => {
    if (!session || !prompt.trim()) {
      setError('Please enter a prompt');
      return;
    }

    if (!currentDesign) {
      await handleCreateDesign();
      if (!currentDesign) return;
    }

    setGenerating(true);
    setError(null);

    try {
      // Use fashionConceptSessionId if available (from existing or newly generated concept)
      // Otherwise fallback to FashionDesignStudio's internal sessionId
      const effectiveSessionId = fashionConceptSessionId || session.sessionId;

      const result = await fashionDesignStudioService.generateImage({
        sessionId: effectiveSessionId,
        designId: currentDesign.id,
        operation: 'text-to-image',
        model,
        prompt,
        userId: user?.id,  // Pass userId for asset persistence
        parameters: {
          width: 1024,
          height: 1536,
          numImages,
          guidanceScale,
          seed,
          style,
        },
        improvePrompt: improvePrompts,
      });

      setGeneratedImages(result.images.map(img => img.url));

      if (result.improvedPrompt) {
        setPrompt(result.improvedPrompt);
      }
    } catch (err: any) {
      setError('Generation failed: ' + err.message);
    } finally {
      setGenerating(false);
    }
  };

  const handleSketchToImage = async () => {
    if (!session || !imagePreview || !prompt.trim()) {
      setError('Please upload a sketch and enter a prompt');
      return;
    }

    if (!currentDesign) {
      await handleCreateDesign();
      if (!currentDesign) return;
    }

    setGenerating(true);
    setError(null);

    try {
      // Use fashionConceptSessionId if available (from existing or newly generated concept)
      const effectiveSessionId = fashionConceptSessionId || session.sessionId;

      const result = await fashionDesignStudioService.sketchToImage({
        sessionId: effectiveSessionId,
        designId: currentDesign.id,
        operation: 'sketch-to-image',
        model: 'flux-kontext', // FLUX Kontext for best sketch-to-fashion quality (upgraded from sdxl-controlnet-canny)
        prompt: prompt,
        sketchImage: imagePreview,
        userId: user?.id,  // Pass userId for asset persistence
        parameters: {
          width: 1024,
          height: 1024,
          numImages: 1,
          seed
        },
        improvePrompt: improvePrompts
      });

      setGeneratedImages(result.images.map(img => img.url));
    } catch (err: any) {
      setError('Sketch conversion failed: ' + err.message);
    } finally {
      setGenerating(false);
    }
  };

  const handleClothingSwap = async (designImage: string) => {
    if (!session || !currentDesign || !prompt.trim()) {
      setError('Please select a design and enter context prompt');
      return;
    }

    setGenerating(true);
    setError(null);

    try {
      // Use fashionConceptSessionId if available (from existing or newly generated concept)
      const effectiveSessionId = fashionConceptSessionId || session.sessionId;

      const result = await fashionDesignStudioService.swapClothing({
        sessionId: effectiveSessionId,
        designId: currentDesign.id,
        operation: 'clothing-swap',
        model: 'flux-pro-kontext', // Context-aware editing for fashion
        prompt: prompt,
        referenceImage: designImage,
        userId: user?.id,  // Pass userId for asset persistence
        parameters: {
          width: 1024,
          height: 1024,
          numImages: 1,
          contextStrength: contextStrength,
          preserveStructure: preserveStructure,
          seed
        }
      });

      setGeneratedImages(result.images.map(img => img.url));
    } catch (err: any) {
      setError('Clothing swap failed: ' + err.message);
    } finally {
      setGenerating(false);
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAnimateDesign = async () => {
    if (!selectedImageForVideo) {
      setError('Please select an image to animate');
      return;
    }

    setGeneratingVideo(true);
    setError(null);
    setGeneratedVideoUrl(null);
    setVideoResponse(null);

    try {
      // Use fashionConceptSessionId if available (from existing or newly generated concept)
      const effectiveSessionId = fashionConceptSessionId || session?.sessionId;

      const result = await videoGenerationService.animateDesign(
        selectedImageForVideo,
        videoPrompt || 'Smooth fabric movement, gentle camera pan, professional fashion presentation',
        {
          model: videoModel,
          duration: videoDuration,
          seed,
          sessionId: effectiveSessionId,
          userId: user?.id
        }
      );

      setGeneratedVideoUrl(result.videoUrl || null);
      setVideoResponse(result);
    } catch (err: any) {
      setError('Animation failed: ' + err.message);
    } finally {
      setGeneratingVideo(false);
    }
  };

  const handleGenerate360View = async () => {
    if (!selectedImageForVideo) {
      setError('Please select an image to generate 360° view');
      return;
    }

    setGeneratingVideo(true);
    setError(null);
    setGeneratedVideoUrl(null);
    setVideoResponse(null);

    try {
      // Use fashionConceptSessionId if available (from existing or newly generated concept)
      const effectiveSessionId = fashionConceptSessionId || session?.sessionId;

      const result = await videoGenerationService.generate360View(
        selectedImageForVideo,
        {
          model: videoModel,
          duration: videoDuration || 8,
          seed,
          sessionId: effectiveSessionId,
          userId: user?.id
        }
      );

      setGeneratedVideoUrl(result.videoUrl || null);
      setVideoResponse(result);
    } catch (err: any) {
      setError('360° view generation failed: ' + err.message);
    } finally {
      setGeneratingVideo(false);
    }
  };

  const handleDownloadVideo = (videoUrl: string) => {
    const a = document.createElement('a');
    a.href = videoUrl;
    a.download = `fashion-video-${Date.now()}.mp4`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const usePromptTemplate = (template: any) => {
    setPrompt(template.template);
    setCategory(template.category);
  };

  const handleSaveSession = async () => {
    if (!session) return;

    setLoading(true);
    try {
      await fashionDesignStudioService.saveSession({ session });
      alert('Session saved successfully!');
    } catch (err: any) {
      setError('Failed to save session: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Multi-agent concept generation (from FashionMultiAgent)
  const handleGenerateConcept = async () => {
    if (!designBrief.trim()) {
      setError('Please enter a design brief');
      return;
    }

    setLoading(true);
    setError(null);
    setTrendAnalysis('');
    setDesignConcept('');
    setTechnicalSpecs('');

    try {
      const request: MultiFashionRequest = {
        designBrief: designBrief.trim(),
        targetMarket: targetMarket || undefined,
        season: season || undefined,
        pricePoint: pricePoint || undefined,
        themeElements: themeElements.length > 0 ? themeElements : undefined,
        colorPalette: colorPalette.length > 0 ? colorPalette : undefined,
        fabricPreferences: fabricPreferences.trim() || undefined,
        includeSustainability,
        iterations,
        maxTokensPerAgentTurn: maxTokens,
        // Provider-aware settings (use UI-selected model from ModelSelector)
        model: selectedLlmModel,
        temperature: 0.7,
        maxTokens: maxTokens
      };

      const response = await FashionService.generateFashionDesign(request);

      if (response) {
        setTrendAnalysis(response.trendAnalysis || '');
        setDesignConcept(response.designConcept || '');
        setTechnicalSpecs(response.technicalSpecifications || '');

        // Store the Fashion Design Agent sessionId for image generation
        if (response.sessionId) {
          setFashionConceptSessionId(response.sessionId);
          console.log('[FashionDesignStudio] Generated concept with sessionId:', response.sessionId);
        }

        // Automatically populate prompt with full content from all sections
        const fullPrompt = [
          response.trendAnalysis && `TREND ANALYSIS:\n${response.trendAnalysis}`,
          response.designConcept && `\n\nDESIGN CONCEPT:\n${response.designConcept}`,
          response.technicalSpecifications && `\n\nTECHNICAL SPECIFICATIONS:\n${response.technicalSpecifications}`
        ].filter(Boolean).join('');

        if (fullPrompt) {
          setPrompt(fullPrompt);
        }

        // Switch to image generation tab
        setActiveTab(2);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate fashion concept');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const handleDownloadComplete = () => {
    const content = `# Fashion Design Collection

## Creative Vision
${designBrief}

${targetMarket ? `**Target Market:** ${targetMarket}\n` : ''}
${season ? `**Season:** ${season}\n` : ''}
${pricePoint ? `**Price Point:** ${pricePoint}\n` : ''}
${themeElements.length > 0 ? `**Theme Elements:** ${themeElements.join(', ')}\n` : ''}
${colorPalette.length > 0 ? `**Color Palette:** ${colorPalette.join(', ')}\n` : ''}
${fabricPreferences ? `**Fabric Preferences:** ${fabricPreferences}\n` : ''}
${includeSustainability ? '**Sustainability:** Included\n' : ''}

## Trend Analysis
${trendAnalysis}

## Design Concept
${designConcept}

## Technical Specifications
${technicalSpecs}

## Generated Images
${generatedImages.map((url, i) => `- [Image ${i + 1}](${url})`).join('\n')}
`;

    const blob = new Blob([content], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `fashion-design-${Date.now()}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadImage = (imageUrl: string, index: number) => {
    // For data URIs (base64 images), download directly without opening in new tab
    if (imageUrl.startsWith('data:')) {
      const a = document.createElement('a');
      a.href = imageUrl;
      a.download = `fashion-design-${model}-${Date.now()}-${index + 1}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } else {
      // For regular URLs, fetch and download
      fetch(imageUrl)
        .then(res => res.blob())
        .then(blob => {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `fashion-design-${model}-${Date.now()}-${index + 1}.png`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        })
        .catch(err => {
          console.error('Download error:', err);
          setError('Failed to download image');
        });
    }
  };

  // Virtual Try-On handlers
  const handleHumanImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setHumanImageFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setHumanImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleGarmentImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setGarmentImageFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setGarmentImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleVirtualTryOn = async () => {
    if (!session) {
      setError('Please initialize a session first');
      return;
    }

    console.log('[Virtual Try-On Component] Validation:', {
      hasSession: !!session,
      hasHumanImage: !!humanImagePreview,
      humanImagePreviewLength: humanImagePreview?.length || 0,
      hasGarmentImage: !!garmentImagePreview,
      garmentImagePreviewLength: garmentImagePreview?.length || 0,
      model: tryOnModel
    });

    if (!humanImagePreview || !garmentImagePreview) {
      setError('Please upload both model image and garment image');
      return;
    }

    if (tryOnModel === 'idm-vton' && !garmentDescription.trim()) {
      setError('Garment description is required for IDM-VTON model');
      return;
    }

    setGeneratingTryOn(true);
    setError(null);
    setTryOnResults([]);
    setTryOnMask(null);
    setHasNsfwConcepts(undefined);

    try {
      const effectiveSessionId = fashionConceptSessionId || session?.sessionId;

      const result = await fashionDesignStudioService.virtualTryOn({
        sessionId: effectiveSessionId,
        userId: user?.id,
        model: tryOnModel,
        humanImage: humanImagePreview,
        garmentImage: garmentImagePreview,

        // IDM-VTON specific
        garmentDescription: tryOnModel === 'idm-vton' ? garmentDescription : undefined,
        numInferenceSteps: tryOnModel === 'idm-vton' ? tryOnInferenceSteps :
                          (tryOnModel === 'cat-vton' || tryOnModel === 'leffa' ? tryOnInferenceSteps : undefined),

        // FASHN specific (V1.5 and V1.6)
        category: (tryOnModel === 'fashn-v16' || tryOnModel === 'fashn-v15') ? tryOnCategory : undefined,
        mode: (tryOnModel === 'fashn-v16' || tryOnModel === 'fashn-v15') ? tryOnMode : undefined,
        garmentPhotoType: (tryOnModel === 'fashn-v16' || tryOnModel === 'fashn-v15') ? garmentPhotoType : undefined,

        // CAT-VTON specific
        catGarmentType: tryOnModel === 'cat-vton' ? catGarmentType : undefined,
        guidanceScale: (tryOnModel === 'cat-vton' || tryOnModel === 'leffa') ? tryOnGuidanceScale : undefined,

        // Leffa specific
        leffaGarmentType: tryOnModel === 'leffa' ? leffaGarmentType : undefined,
        enableSafetyChecker: tryOnModel === 'leffa' ? enableSafetyChecker : undefined,

        // Common
        seed,
      });

      setTryOnResults(result.images.map(img => img.url));
      if (result.mask) {
        setTryOnMask(result.mask);
      }
      if (result.hasNsfwConcepts !== undefined) {
        setHasNsfwConcepts(result.hasNsfwConcepts);
      }

    } catch (err: any) {
      console.error('Virtual Try-On error:', err);
      setError(err.message || 'Virtual try-on failed');
    } finally {
      setGeneratingTryOn(false);
    }
  };

  const selectGeneratedImageForTryOn = (imageUrl: string, type: 'human' | 'garment') => {
    if (type === 'human') {
      setHumanImagePreview(imageUrl);
      setHumanImageFile(null);
    } else {
      setGarmentImagePreview(imageUrl);
      setGarmentImageFile(null);
    }
  };

  if (loading && !session) {
    return (
      <Container maxWidth="xl" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Loading Fashion Design Studio...
        </Typography>
      </Container>
    );
  }

  return (
    <Container
      maxWidth="lg"
      sx={{
        py: { xs: 2, sm: 3, md: 4 },
        px: { xs: 1.5, sm: 2, md: 3 },
        width: '100%',
        maxWidth: '100%',
        overflow: 'hidden'
      }}
    >
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: { xs: 2, sm: 3, md: 4 } }}>
        <Typography
          variant="h4"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: { xs: 0.5, sm: 1 },
            mb: { xs: 1, sm: 2 },
            fontSize: { xs: '1.5rem', sm: '1.875rem', md: '2.125rem' },
            flexWrap: 'wrap'
          }}
        >
          <CheckroomIcon sx={{ fontSize: { xs: 32, sm: 40 } }} />
          {isMobile ? 'Fashion Studio' : 'Fashion Design Studio'}
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{
            fontSize: { xs: '0.875rem', sm: '1rem' },
            px: { xs: 0.5, sm: 0 }
          }}
        >
          {isMobile
            ? 'Professional fashion design with AI'
            : 'Professional fashion design powered by FLUX Krea & Kontext'}
        </Typography>

        <Box sx={{ mt: { xs: 1.5, sm: 2 }, display: 'flex', gap: { xs: 1, sm: 2 }, flexWrap: 'wrap', justifyContent: 'center' }}>
          <Chip icon={<ImageIcon />} label="FLUX Krea" color="primary" size={isMobile ? 'small' : 'medium'} />
          <Chip icon={<PaletteIcon />} label={isMobile ? 'Context' : 'Context-Aware Editing'} color="secondary" size={isMobile ? 'small' : 'medium'} />
          <Chip icon={<AnimateIcon />} label="Animation" size={isMobile ? 'small' : 'medium'} />
          <Chip icon={<View360Icon />} label="360° Views" size={isMobile ? 'small' : 'medium'} />
          <Chip label={`${designs.length} Designs`} size={isMobile ? 'small' : 'medium'} />
        </Box>
      </Box>

      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 2,
            borderRadius: 1.5,
            fontSize: { xs: '0.875rem', sm: '1rem' }
          }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {/* Tabs */}
      <Paper
        elevation={0}
        sx={{
          border: 1,
          borderColor: 'divider',
          borderRadius: { xs: 1, sm: 2 },
          mb: { xs: 2, sm: 3 },
          overflow: { xs: 'visible', sm: 'hidden' }
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          variant={isMobile ? "scrollable" : "fullWidth"}
          scrollButtons={isMobile ? "auto" : false}
          allowScrollButtonsMobile
          sx={{
            minHeight: { xs: 48, sm: 56 },
            '& .MuiTab-root': {
              textTransform: 'none',
              fontSize: { xs: '0.8125rem', sm: '0.9375rem' },
              fontWeight: 500,
              minHeight: { xs: 48, sm: 56 },
              minWidth: { xs: 80, sm: 'auto' },
              px: { xs: 1.5, sm: 2 },
              flex: { xs: 'none', sm: 1 },
              '& .MuiSvgIcon-root': {
                fontSize: { xs: '1.125rem', sm: '1.25rem' },
                display: { xs: 'none', sm: 'block' }
              }
            },
            '& .MuiTabs-flexContainer': {
              justifyContent: { xs: 'flex-start', sm: 'space-between' },
              gap: { xs: 0.5, sm: 0 }
            },
            '& .MuiTabs-scrollButtons': {
              width: { xs: 32, sm: 40 },
              '&.Mui-disabled': {
                opacity: 0.3
              }
            },
            '& .MuiTabs-indicator': {
              height: { xs: 2, sm: 3 },
              borderRadius: { xs: '2px 2px 0 0', sm: '3px 3px 0 0' }
            }
          }}
        >
          <Tab
            {...(!isMobile && { iconPosition: "start", icon: <BriefIcon /> })}
            label="Inspiration"
          />
          <Tab
            {...(!isMobile && { iconPosition: "start", icon: <ConfigIcon /> })}
            label="Config"
          />
          <Tab
            {...(!isMobile && { iconPosition: "start", icon: <MagicIcon /> })}
            label="Generate"
          />
          <Tab
            {...(!isMobile && { iconPosition: "start", icon: <PaletteIcon /> })}
            label="Styling"
          />
          <Tab
            {...(!isMobile && { iconPosition: "start", icon: <AnimateIcon /> })}
            label={isMobile ? "Animate" : "Animation"}
          />
          <Tab
            {...(!isMobile && { iconPosition: "start", icon: <View360Icon /> })}
            label={isMobile ? "360°" : "360 View"}
          />
          <Tab
            {...(!isMobile && { iconPosition: "start", icon: <VirtualTryOnIcon /> })}
            label={isMobile ? "Try-On" : "Virtual Try-On"}
          />
        </Tabs>
      </Paper>

      {/* Tab Panels */}
      <TabPanel value={activeTab} index={0}>
        {/* Inspiration Tab - Multi-agent concept generation */}
        <Grid container spacing={{ xs: 2, sm: 3 }}>
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1.0625rem', sm: '1.25rem' } }}>
                Creative Vision
              </Typography>

              {/* Toggle between new and existing concept */}
              <Box sx={{ mb: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={useExistingConcept}
                      onChange={(e) => {
                        setUseExistingConcept(e.target.checked);
                        if (!e.target.checked) {
                          // Clear selection when switching to "Create New"
                          setSelectedSessionId('');
                        }
                      }}
                    />
                  }
                  label="Use Existing Fashion Design Concept"
                />
              </Box>

              {/* Existing session selector */}
              {useExistingConcept && (
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Select Design Concept</InputLabel>
                  <Select
                    value={selectedSessionId}
                    onChange={(e) => handleSelectExistingSession(e.target.value)}
                    label="Select Design Concept"
                    disabled={loadingFashionSessions}
                  >
                    {loadingFashionSessions ? (
                      <MenuItem disabled>
                        <CircularProgress size={20} sx={{ mr: 1 }} />
                        Loading sessions...
                      </MenuItem>
                    ) : fashionSessions.length === 0 ? (
                      <MenuItem disabled>No fashion design concepts found</MenuItem>
                    ) : (
                      fashionSessions.map((session) => {
                        // Build secondary info array with available fields
                        const secondaryInfo = [];
                        if (session.season) secondaryInfo.push(session.season);
                        if (session.targetMarket) secondaryInfo.push(session.targetMarket);
                        if (session.pricePoint) secondaryInfo.push(session.pricePoint);

                        // Always show created date
                        const formattedDate = new Date(session.createdAt).toLocaleDateString();

                        // If no optional fields, show "Created: [date]" for clarity
                        const secondaryText = secondaryInfo.length > 0
                          ? `${secondaryInfo.join(' • ')} • ${formattedDate}`
                          : `Created: ${formattedDate}`;

                        return (
                          <MenuItem key={session.id} value={session.id}>
                            <Box sx={{ width: '100%' }}>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {session.designBrief.length > 60
                                  ? `${session.designBrief.substring(0, 60)}...`
                                  : session.designBrief}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {secondaryText}
                              </Typography>
                            </Box>
                          </MenuItem>
                        );
                      })
                    )}
                  </Select>
                </FormControl>
              )}

              <TextField
                fullWidth
                multiline
                rows={3}
                label="Creative Vision"
                placeholder="Describe your fashion collection concept..."
                value={designBrief}
                onChange={(e) => setDesignBrief(e.target.value)}
                required
                disabled={useExistingConcept}
                helperText={
                  useExistingConcept
                    ? "Select a design concept from the dropdown above to auto-fill this field"
                    : "Describe the vision, inspiration, and goals for your fashion collection"
                }
                sx={{ mb: 2 }}
              />

              <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Target Market</InputLabel>
                    <Select
                      value={targetMarket}
                      onChange={(e) => setTargetMarket(e.target.value)}
                      label="Target Market"
                      disabled={useExistingConcept}
                    >
                      <MenuItem value="">None</MenuItem>
                      {TARGET_MARKETS.map((market) => (
                        <MenuItem key={market} value={market}>
                          {market}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Season</InputLabel>
                    <Select
                      value={season}
                      onChange={(e) => setSeason(e.target.value)}
                      label="Season"
                      disabled={useExistingConcept}
                    >
                      <MenuItem value="">None</MenuItem>
                      {SEASONS.map((s) => (
                        <MenuItem key={s} value={s}>
                          {s}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Price Point</InputLabel>
                    <Select
                      value={pricePoint}
                      onChange={(e) => setPricePoint(e.target.value)}
                      label="Price Point"
                      disabled={useExistingConcept}
                    >
                      <MenuItem value="">None</MenuItem>
                      {PRICE_POINTS.map((price) => (
                        <MenuItem key={price} value={price}>
                          {price}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Theme Elements
                </Typography>
                <Autocomplete
                  multiple
                  options={THEME_SUGGESTIONS}
                  value={themeElements}
                  onChange={(_, newValue) => setThemeElements(newValue)}
                  freeSolo
                  disabled={useExistingConcept}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        variant="outlined"
                        label={option}
                        {...getTagProps({ index })}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      placeholder="Select or add theme elements"
                    />
                  )}
                />
              </Box>

              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PaletteIcon fontSize="small" /> Color Palette
                </Typography>
                <Autocomplete
                  multiple
                  options={COLOR_SUGGESTIONS}
                  value={colorPalette}
                  onChange={(_, newValue) => setColorPalette(newValue)}
                  freeSolo
                  disabled={useExistingConcept}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        variant="outlined"
                        label={option}
                        {...getTagProps({ index })}
                        sx={{ backgroundColor: 'rgba(0,0,0,0.05)' }}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      placeholder="Select or add colors"
                    />
                  )}
                />
              </Box>

              <TextField
                fullWidth
                multiline
                rows={2}
                label="Fabric Preferences"
                placeholder="e.g., Organic cotton, silk blends, technical fabrics..."
                value={fabricPreferences}
                onChange={(e) => setFabricPreferences(e.target.value)}
                disabled={useExistingConcept}
                sx={{ mt: 2 }}
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={includeSustainability}
                    onChange={(e) => setIncludeSustainability(e.target.checked)}
                    disabled={useExistingConcept}
                  />
                }
                label="Include sustainability considerations"
                sx={{ mt: 2 }}
              />

              <Box sx={{ mt: 3 }}>
                <ModelSelector
                  value={selectedLlmModel}
                  onChange={(model) => {
                    setSelectedLlmModel(model);
                    localStorage.setItem('fashionLlmModel', model);
                  }}
                  label="LLM Model for Concept Generation"
                />
              </Box>

              <Box sx={{ mt: 2 }}>
                <Typography gutterBottom>
                  Agent Iterations: {iterations}
                </Typography>
                <Slider
                  value={iterations}
                  onChange={(_, value) => setIterations(value as number)}
                  min={1}
                  max={5}
                  marks
                  valueLabelDisplay="auto"
                />
              </Box>

              <Button
                variant="contained"
                size="large"
                onClick={handleGenerateConcept}
                disabled={loading || !designBrief.trim()}
                startIcon={loading ? <CircularProgress size={20} /> : <CheckroomIcon />}
                fullWidth
                sx={{ mt: 3 }}
              >
                {loading ? 'Generating Concept...' : 'Generate Fashion Concept'}
              </Button>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1.0625rem', sm: '1.25rem' } }}>
                Generated Concept
              </Typography>

              {(trendAnalysis || designConcept || technicalSpecs) ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {trendAnalysis && (
                    <Card variant="outlined">
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Typography variant="subtitle2">Trend Analysis</Typography>
                          <IconButton onClick={() => handleCopy(trendAnalysis)} size="small">
                            <CopyIcon fontSize="small" />
                          </IconButton>
                        </Box>
                        <Typography
                          variant="body2"
                          sx={{
                            whiteSpace: 'pre-wrap',
                            fontSize: '0.85rem',
                            maxHeight: 150,
                            overflow: 'auto'
                          }}
                        >
                          {trendAnalysis.substring(0, 200)}...
                        </Typography>
                      </CardContent>
                    </Card>
                  )}

                  {designConcept && (
                    <Card variant="outlined">
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Typography variant="subtitle2">Design Concept</Typography>
                          <IconButton onClick={() => handleCopy(designConcept)} size="small">
                            <CopyIcon fontSize="small" />
                          </IconButton>
                        </Box>
                        <Typography
                          variant="body2"
                          sx={{
                            whiteSpace: 'pre-wrap',
                            fontSize: '0.85rem',
                            maxHeight: 150,
                            overflow: 'auto'
                          }}
                        >
                          {designConcept.substring(0, 200)}...
                        </Typography>
                      </CardContent>
                    </Card>
                  )}

                  {technicalSpecs && (
                    <Card variant="outlined">
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Typography variant="subtitle2">Technical Specs</Typography>
                          <IconButton onClick={() => handleCopy(technicalSpecs)} size="small">
                            <CopyIcon fontSize="small" />
                          </IconButton>
                        </Box>
                        <Typography
                          variant="body2"
                          sx={{
                            whiteSpace: 'pre-wrap',
                            fontSize: '0.85rem',
                            maxHeight: 150,
                            overflow: 'auto'
                          }}
                        >
                          {technicalSpecs.substring(0, 200)}...
                        </Typography>
                      </CardContent>
                    </Card>
                  )}

                  <Button
                    startIcon={<DownloadIcon />}
                    onClick={handleDownloadComplete}
                    variant="outlined"
                    fullWidth
                  >
                    Download Complete
                  </Button>

                  <Alert severity="success" icon={<CheckIcon />}>
                    Concept ready! Go to "Generate" tab to create images.
                  </Alert>
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                  <CheckroomIcon sx={{ fontSize: 64, opacity: 0.3 }} />
                  <Typography variant="body2" sx={{ mt: 2 }}>
                    No concept generated yet
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={activeTab} index={1}>
        {/* Config Tab - Design Configuration */}
        <Grid container spacing={{ xs: 2, sm: 3 }}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1.0625rem', sm: '1.25rem' } }}>
                Design Configuration
              </Typography>

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Category</InputLabel>
                <Select value={category} onChange={(e) => setCategory(e.target.value as FashionCategory)}>
                  <MenuItem value="haute-couture">Haute Couture</MenuItem>
                  <MenuItem value="ready-to-wear">Ready-to-Wear</MenuItem>
                  <MenuItem value="streetwear">Streetwear</MenuItem>
                  <MenuItem value="sportswear">Sportswear</MenuItem>
                  <MenuItem value="evening-wear">Evening Wear</MenuItem>
                  <MenuItem value="accessories">Accessories</MenuItem>
                  <MenuItem value="footwear">Footwear</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Style</InputLabel>
                <Select value={style} onChange={(e) => setStyle(e.target.value as FashionStyle)}>
                  <MenuItem value="editorial">Editorial</MenuItem>
                  <MenuItem value="catalog">Catalog</MenuItem>
                  <MenuItem value="runway">Runway</MenuItem>
                  <MenuItem value="street-style">Street Style</MenuItem>
                  <MenuItem value="studio">Studio</MenuItem>
                  <MenuItem value="lifestyle">Lifestyle</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Model</InputLabel>
                <Select
                  value={model}
                  onChange={(e) => setModel(e.target.value as ImageModel)}
                >
                  {/* Text-to-Image Models (Recommended for Generation) */}
                  <MenuItem disabled sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    📝 Text-to-Image (Generate from Prompt)
                  </MenuItem>
                  <MenuItem value="flux-krea">⭐ FLUX Krea (Best for Fashion - $0.030)</MenuItem>
                  <MenuItem value="flux-pro">FLUX Pro v1.1 (Premium Quality - $0.055)</MenuItem>
                  <MenuItem value="flux-pro-ultra">FLUX Pro Ultra (Highest 2K - $0.080)</MenuItem>
                  <MenuItem value="flux-dev">FLUX Dev (Balanced - $0.025)</MenuItem>
                  <MenuItem value="flux-schnell">FLUX Schnell (Fastest - $0.003)</MenuItem>
                  <MenuItem value="fast-lightning-sdxl">SDXL Lightning (Ultra-Fast - $0.008)</MenuItem>
                  <MenuItem value="sd35-large">SD 3.5 Large (High Quality - $0.030)</MenuItem>

                  {/* Image-to-Image Models (Require Reference Image) */}
                  <MenuItem disabled sx={{ fontWeight: 'bold', color: 'secondary.main', mt: 2 }}>
                    🖼️ Image-to-Image (Transform Existing)
                  </MenuItem>
                  <MenuItem value="flux-krea-dev-redux">FLUX Krea Redux (Style Transfer - $0.035)</MenuItem>
                  <MenuItem value="flux-dev-redux">FLUX Dev Redux (Transform - $0.035)</MenuItem>
                  <MenuItem value="flux-pro-redux">FLUX Pro Redux (Premium Transform - $0.050)</MenuItem>
                  <MenuItem value="flux-dev-image-to-image">FLUX Dev I2I (Image Editing - $0.030)</MenuItem>
                  <MenuItem value="flux-schnell-redux">FLUX Schnell Redux (Fast Transform - $0.010)</MenuItem>

                  {/* Specialized Models */}
                  <MenuItem disabled sx={{ fontWeight: 'bold', color: 'success.main', mt: 2 }}>
                    🎯 Specialized
                  </MenuItem>
                  <MenuItem value="flux-pro-kontext">FLUX Pro Kontext (Context-Aware - $0.060)</MenuItem>
                  <MenuItem value="sdxl-controlnet-canny">SDXL Canny (Sketch-to-Image - $0.015)</MenuItem>
                </Select>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  {model.includes('redux') || model.includes('image-to-image') || model.includes('kontext')
                    ? '🖼️ This model works best with a reference image (upload in Styling tab)'
                    : '📝 This model generates images directly from text prompts'}
                </Typography>
              </FormControl>

              <Button
                variant="contained"
                startIcon={<AddIcon />}
                fullWidth
                onClick={handleCreateDesign}
                disabled={loading}
              >
                Create New Design
              </Button>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1.0625rem', sm: '1.25rem' } }}>
                Prompt Templates
              </Typography>
              <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                {FASHION_PROMPT_TEMPLATES.map((template) => (
                  <Card key={template.id} sx={{ mb: 2 }}>
                    <CardContent>
                      <Typography variant="subtitle2" gutterBottom>
                        {template.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {template.template.substring(0, 100)}...
                      </Typography>
                    </CardContent>
                    <CardActions>
                      <Button size="small" onClick={() => usePromptTemplate(template)}>
                        Use Template
                      </Button>
                    </CardActions>
                  </Card>
                ))}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={activeTab} index={2}>
        {/* Generate Tab - Text-to-Image & Sketch-to-Image */}
        <Grid container spacing={{ xs: 2, sm: 3 }}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1.0625rem', sm: '1.25rem' } }}>
                Generate Design
              </Typography>

              <TextField
                fullWidth
                multiline
                minRows={6}
                maxRows={20}
                label="Design Prompt"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="Describe your fashion design..."
                helperText="Enter your complete creative vision - no character limit"
                sx={{ mb: 2 }}
              />

              <Box sx={{ mb: 2 }}>
                <Typography gutterBottom>Number of Images: {numImages}</Typography>
                <Slider
                  value={numImages}
                  onChange={(_, v) => setNumImages(v as number)}
                  min={1}
                  max={8}
                  marks
                  valueLabelDisplay="auto"
                />
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography gutterBottom>Guidance Scale: {guidanceScale.toFixed(1)}</Typography>
                <Slider
                  value={guidanceScale}
                  onChange={(_, v) => setGuidanceScale(v as number)}
                  min={1}
                  max={15}
                  step={0.5}
                  valueLabelDisplay="auto"
                />
              </Box>

              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography gutterBottom sx={{ mb: 0, flexGrow: 1 }}>
                    Seed: {seed}
                  </Typography>
                  <Tooltip title="Generate random seed">
                    <IconButton
                      size="small"
                      onClick={() => setSeed(Math.floor(Math.random() * 2147483647))}
                    >
                      <RefreshIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
                <TextField
                  fullWidth
                  type="number"
                  value={seed}
                  onChange={(e) => setSeed(parseInt(e.target.value) || 0)}
                  size="small"
                  inputProps={{ min: 0, max: 2147483647 }}
                  helperText="Use same seed for reproducible results"
                />
              </Box>

              <Box sx={{ mb: 2, display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  startIcon={generating ? <CircularProgress size={20} /> : <ImageIcon />}
                  onClick={handleTextToImage}
                  disabled={generating || !prompt.trim()}
                  fullWidth
                >
                  Generate from Text
                </Button>
                <Tooltip title={improvePrompts ? 'Prompts will be improved' : 'Prompts will not be improved'}>
                  <IconButton
                    color={improvePrompts ? 'primary' : 'default'}
                    onClick={() => setImprovePrompts(!improvePrompts)}
                  >
                    <MagicIcon />
                  </IconButton>
                </Tooltip>
              </Box>

              <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                Or Upload Sketch
              </Typography>

              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="sketch-upload"
                type="file"
                onChange={handleFileUpload}
              />
              <label htmlFor="sketch-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<UploadIcon />}
                  fullWidth
                  sx={{ mb: 2 }}
                >
                  Upload Sketch
                </Button>
              </label>

              {imagePreview && (
                <>
                  <Box sx={{ mb: 2, textAlign: 'center' }}>
                    <img
                      src={imagePreview}
                      alt="Sketch preview"
                      style={{ maxWidth: '100%', maxHeight: 200, objectFit: 'contain' }}
                    />
                  </Box>
                  <Button
                    variant="contained"
                    startIcon={generating ? <CircularProgress size={20} /> : <ImageIcon />}
                    onClick={handleSketchToImage}
                    disabled={generating || !prompt.trim()}
                    fullWidth
                  >
                    Convert Sketch to Design
                  </Button>
                </>
              )}
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1.0625rem', sm: '1.25rem' } }}>
                Generated Images
              </Typography>

              {generating && (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <CircularProgress size={60} />
                  <Typography variant="body2" sx={{ mt: 2 }}>
                    Generating your design...
                  </Typography>
                  <LinearProgress sx={{ mt: 2 }} />
                </Box>
              )}

              {generatedImages.length > 0 && (
                <ImageList cols={2} gap={8}>
                  {generatedImages.map((url, index) => (
                    <ImageListItem key={index}>
                      <img src={url} alt={`Generated ${index + 1}`} loading="lazy" />
                      <ImageListItemBar
                        actionIcon={
                          <IconButton
                            sx={{ color: 'rgba(255, 255, 255, 0.8)' }}
                            onClick={() => handleDownloadImage(url, index)}
                            title="Download image"
                          >
                            <DownloadIcon />
                          </IconButton>
                        }
                      />
                    </ImageListItem>
                  ))}
                </ImageList>
              )}

              {!generating && generatedImages.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                  <ImageIcon sx={{ fontSize: 64, opacity: 0.3 }} />
                  <Typography>No images generated yet</Typography>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={activeTab} index={3}>
        {/* Styling Tab - Context & Clothing Swap */}
        <Paper sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1.0625rem', sm: '1.25rem' } }}>
            Context & Styling (FLUX Kontext)
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            Select a generated design and describe the new context or clothing style to apply
          </Alert>

          <TextField
            fullWidth
            multiline
            minRows={4}
            maxRows={15}
            label="Context/Style Prompt"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="E.g., 'Place on a runway with dramatic lighting' or 'Swap to evening gown style'"
            helperText="Describe the styling transformation - no character limit"
            sx={{ mb: 2 }}
          />

          <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 2 }}>
                <Typography gutterBottom>
                  Context Strength: {contextStrength.toFixed(2)}
                </Typography>
                <Slider
                  value={contextStrength}
                  onChange={(_, v) => setContextStrength(v as number)}
                  min={0.5}
                  max={1.0}
                  step={0.05}
                  marks={[
                    { value: 0.5, label: '0.5' },
                    { value: 0.75, label: '0.75' },
                    { value: 1.0, label: '1.0' }
                  ]}
                  valueLabelDisplay="auto"
                />
                <Typography variant="caption" color="text.secondary">
                  Higher values preserve more of the original image context
                </Typography>
              </Box>

              <FormControlLabel
                control={
                  <Switch
                    checked={preserveStructure}
                    onChange={(e) => setPreserveStructure(e.target.checked)}
                  />
                }
                label="Preserve Structure"
              />
              <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4 }}>
                Maintain the original composition and layout
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography gutterBottom sx={{ mb: 0, flexGrow: 1 }}>
                    Seed: {seed}
                  </Typography>
                  <Tooltip title="Generate random seed">
                    <IconButton
                      size="small"
                      onClick={() => setSeed(Math.floor(Math.random() * 2147483647))}
                    >
                      <RefreshIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
                <TextField
                  fullWidth
                  type="number"
                  value={seed}
                  onChange={(e) => setSeed(parseInt(e.target.value) || 0)}
                  size="small"
                  inputProps={{ min: 0, max: 2147483647 }}
                  helperText="Use same seed for reproducible results"
                />
              </Box>
            </Grid>
          </Grid>

          {generatedImages.length > 0 && (
            <Grid container spacing={{ xs: 1.5, sm: 2 }}>
              {generatedImages.map((url, index) => (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  <Card>
                    <CardMedia
                      component="img"
                      height="300"
                      image={url}
                      alt={`Design ${index + 1}`}
                      sx={{
                        objectFit: 'contain',
                        backgroundColor: 'grey.900'
                      }}
                      onError={(e) => {
                        console.error('Error loading fashion design image:', e);
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                      }}
                    />
                    <CardActions>
                      <Button size="small" onClick={() => handleClothingSwap(url)} disabled={generating}>
                        Apply Context
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Paper>
      </TabPanel>

      <TabPanel value={activeTab} index={4}>
        {/* Animation Tab */}
        <Grid container spacing={{ xs: 2, sm: 3 }}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1.0625rem', sm: '1.25rem' } }}>
                Animate Your Design
              </Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                Transform your static fashion designs into dynamic videos with natural movement and fabric physics.
              </Alert>

              {/* Image Selection */}
              <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                Select Image to Animate
              </Typography>
              {generatedImages.length > 0 ? (
                <Grid container spacing={1} sx={{ mb: 2 }}>
                  {generatedImages.map((url, index) => (
                    <Grid item xs={4} key={index}>
                      <Box
                        sx={{
                          border: selectedImageForVideo === url ? 3 : 1,
                          borderColor: selectedImageForVideo === url ? 'primary.main' : 'grey.300',
                          borderRadius: 1,
                          overflow: 'hidden',
                          cursor: 'pointer',
                          '&:hover': { opacity: 0.8 }
                        }}
                        onClick={() => setSelectedImageForVideo(url)}
                      >
                        <img src={url} alt={`Design ${index + 1}`} style={{ width: '100%', display: 'block' }} />
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Generate some images first to animate them
                </Alert>
              )}

              {/* Video Model Selection */}
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Video Model</InputLabel>
                <Select
                  value={videoModel}
                  onChange={(e) => setVideoModel(e.target.value)}
                  label="Video Model"
                  disabled={generatingVideo}
                >
                  <MenuItem value={FASHION_VIDEO_MODELS.HAILUO_STANDARD}>
                    Hailuo Standard - Reliable & Affordable ($0.08/sec)
                  </MenuItem>
                  <MenuItem value={FASHION_VIDEO_MODELS.KLING_STANDARD}>
                    Kling 2.5 Turbo - Fast Quality ($0.12/sec)
                  </MenuItem>
                  <MenuItem value={FASHION_VIDEO_MODELS.HAILUO_PRO}>
                    Hailuo Pro - Enhanced Quality ($0.15/sec)
                  </MenuItem>
                  <MenuItem value={FASHION_VIDEO_MODELS.VEO_31_FAST}>
                    VEO 3.1 Fast - Quick Premium ($0.18/sec)
                  </MenuItem>
                  <MenuItem value={FASHION_VIDEO_MODELS.KLING_PRO}>
                    Kling 2.5 Pro - Professional ($0.20/sec)
                  </MenuItem>
                  <MenuItem value={FASHION_VIDEO_MODELS.WAN_25}>
                    WAN 2.5 - I2V Specialist ($0.20/sec)
                  </MenuItem>
                  <MenuItem value={FASHION_VIDEO_MODELS.VEO_31}>
                    VEO 3.1 - Premium Google ($0.28/sec)
                  </MenuItem>
                  <MenuItem value={FASHION_VIDEO_MODELS.LTX_VIDEO}>
                    LTX Video - Ultra Fast ($0.02/sec)
                  </MenuItem>
                </Select>
              </FormControl>

              {/* Animation Prompt */}
              <TextField
                fullWidth
                multiline
                minRows={4}
                maxRows={12}
                label="Animation Description (Optional)"
                value={videoPrompt}
                onChange={(e) => setVideoPrompt(e.target.value)}
                placeholder="E.g., 'Smooth fabric flowing in the wind, elegant runway walk, slow zoom'"
                sx={{ mb: 2 }}
                disabled={generatingVideo}
                helperText="Describe the motion and camera movement you want - no character limit"
              />

              {/* Duration Control */}
              <Box sx={{ mb: 2 }}>
                <Typography gutterBottom>Duration: {videoDuration} seconds</Typography>
                <Slider
                  value={videoDuration}
                  onChange={(_, v) => setVideoDuration(v as number)}
                  min={3}
                  max={10}
                  marks
                  valueLabelDisplay="auto"
                  disabled={generatingVideo}
                />
              </Box>

              {/* Generate Button */}
              <Button
                variant="contained"
                fullWidth
                startIcon={generatingVideo ? <CircularProgress size={20} /> : <AnimateIcon />}
                onClick={handleAnimateDesign}
                disabled={generatingVideo || !selectedImageForVideo}
              >
                {generatingVideo ? 'Generating Animation...' : 'Animate Design'}
              </Button>

              {generatingVideo && (
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                  <LinearProgress />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                    This may take 1-3 minutes depending on the model...
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1.0625rem', sm: '1.25rem' } }}>
                Animation Result
              </Typography>

              {/* Previously Generated Animation Videos */}
              {loadingConceptVideos && (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                  <CircularProgress size={24} />
                  <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                    Loading previous animations...
                  </Typography>
                </Box>
              )}

              {!loadingConceptVideos && animationVideos.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
                    Previously Generated ({animationVideos.length})
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {animationVideos.map((videoUrl, index) => (
                      <Box key={index}>
                        <Box sx={{ mb: 1, bgcolor: 'black', borderRadius: 1 }}>
                          <video
                            src={videoUrl}
                            controls
                            loop
                            style={{ width: '100%', display: 'block' }}
                          />
                        </Box>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<DownloadIcon />}
                          onClick={() => handleDownloadVideo(videoUrl)}
                          fullWidth
                        >
                          Download Video {index + 1}
                        </Button>
                      </Box>
                    ))}
                  </Box>
                  <Divider sx={{ my: 3 }} />
                </Box>
              )}

              {generatedVideoUrl ? (
                <>
                  <Box sx={{ mb: 2, bgcolor: 'black', borderRadius: 1 }}>
                    <video
                      src={generatedVideoUrl}
                      controls
                      autoPlay
                      loop
                      style={{ width: '100%', display: 'block' }}
                    />
                  </Box>

                  {/* Video Metadata */}
                  {videoResponse && videoResponse.videos && videoResponse.videos[0] && (
                    <Box sx={{
                      mb: 2,
                      p: 2,
                      bgcolor: 'rgba(0, 0, 0, 0.6)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: 1
                    }}>
                      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.light' }}>
                        Video Details
                      </Typography>
                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Model:</Typography>
                          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.95)' }}>{videoResponse.model}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Provider:</Typography>
                          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.95)' }}>{videoResponse.provider}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Resolution:</Typography>
                          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.95)' }}>{videoResponse.videos[0].resolution}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Audio:</Typography>
                          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.95)' }}>
                            {videoResponse.videos[0].hasAudio ? '✓ Yes' : '✗ No'}
                          </Typography>
                        </Grid>
                        {videoResponse.videos[0].duration > 0 && (
                          <Grid item xs={6}>
                            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Duration:</Typography>
                            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.95)' }}>{videoResponse.videos[0].duration}s</Typography>
                          </Grid>
                        )}
                        <Grid item xs={6}>
                          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Format:</Typography>
                          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.95)' }}>{videoResponse.videos[0].contentType}</Typography>
                        </Grid>
                      </Grid>
                    </Box>
                  )}

                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="contained"
                      startIcon={<DownloadIcon />}
                      onClick={() => handleDownloadVideo(generatedVideoUrl)}
                      fullWidth
                    >
                      Download Video
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setGeneratedVideoUrl(null);
                        setVideoResponse(null);
                      }}
                    >
                      Clear
                    </Button>
                  </Box>
                </>
              ) : (
                <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
                  <AnimateIcon sx={{ fontSize: 64, opacity: 0.3 }} />
                  <Typography>No animation generated yet</Typography>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={activeTab} index={5}>
        {/* 360 View Tab */}
        <Grid container spacing={{ xs: 2, sm: 3 }}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1.0625rem', sm: '1.25rem' } }}>
                Generate 360° View
              </Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                Create rotating turntable views of your fashion designs for product showcases and presentations.
              </Alert>

              {/* Image Selection */}
              <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                Select Image for 360° View
              </Typography>
              {generatedImages.length > 0 ? (
                <Grid container spacing={1} sx={{ mb: 2 }}>
                  {generatedImages.map((url, index) => (
                    <Grid item xs={4} key={index}>
                      <Box
                        sx={{
                          border: selectedImageForVideo === url ? 3 : 1,
                          borderColor: selectedImageForVideo === url ? 'primary.main' : 'grey.300',
                          borderRadius: 1,
                          overflow: 'hidden',
                          cursor: 'pointer',
                          '&:hover': { opacity: 0.8 }
                        }}
                        onClick={() => setSelectedImageForVideo(url)}
                      >
                        <img src={url} alt={`Design ${index + 1}`} style={{ width: '100%', display: 'block' }} />
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Generate some images first to create 360° views
                </Alert>
              )}

              {/* Video Model Selection */}
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Video Model</InputLabel>
                <Select
                  value={videoModel}
                  onChange={(e) => setVideoModel(e.target.value)}
                  label="Video Model"
                  disabled={generatingVideo}
                >
                  <MenuItem value={FASHION_VIDEO_MODELS.HAILUO_STANDARD}>
                    Hailuo Standard - Reliable & Affordable ($0.08/sec)
                  </MenuItem>
                  <MenuItem value={FASHION_VIDEO_MODELS.KLING_STANDARD}>
                    Kling 2.5 Turbo - Fast Quality ($0.12/sec)
                  </MenuItem>
                  <MenuItem value={FASHION_VIDEO_MODELS.HAILUO_PRO}>
                    Hailuo Pro - Enhanced Quality ($0.15/sec)
                  </MenuItem>
                  <MenuItem value={FASHION_VIDEO_MODELS.VEO_31_FAST}>
                    VEO 3.1 Fast - Quick Premium ($0.18/sec)
                  </MenuItem>
                  <MenuItem value={FASHION_VIDEO_MODELS.KLING_PRO}>
                    Kling 2.5 Pro - Professional ($0.20/sec)
                  </MenuItem>
                  <MenuItem value={FASHION_VIDEO_MODELS.WAN_25}>
                    WAN 2.5 - I2V Specialist ($0.20/sec)
                  </MenuItem>
                  <MenuItem value={FASHION_VIDEO_MODELS.VEO_31}>
                    VEO 3.1 - Premium Google ($0.28/sec)
                  </MenuItem>
                  <MenuItem value={FASHION_VIDEO_MODELS.LTX_VIDEO}>
                    LTX Video - Ultra Fast ($0.02/sec)
                  </MenuItem>
                </Select>
              </FormControl>

              {/* Duration Control */}
              <Box sx={{ mb: 2 }}>
                <Typography gutterBottom>Duration: {videoDuration || 8} seconds</Typography>
                <Slider
                  value={videoDuration || 8}
                  onChange={(_, v) => setVideoDuration(v as number)}
                  min={5}
                  max={10}
                  marks
                  valueLabelDisplay="auto"
                  disabled={generatingVideo}
                />
                <Typography variant="caption" color="text.secondary">
                  Longer duration = smoother, slower rotation
                </Typography>
              </Box>

              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="caption">
                  The AI will automatically generate a smooth 360° rotation with professional studio lighting.
                  No additional prompts needed!
                </Typography>
              </Alert>

              {/* Generate Button */}
              <Button
                variant="contained"
                fullWidth
                startIcon={generatingVideo ? <CircularProgress size={20} /> : <View360Icon />}
                onClick={handleGenerate360View}
                disabled={generatingVideo || !selectedImageForVideo}
              >
                {generatingVideo ? 'Generating 360° View...' : 'Generate 360° View'}
              </Button>

              {generatingVideo && (
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                  <LinearProgress />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                    This may take 1-3 minutes depending on the model...
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1.0625rem', sm: '1.25rem' } }}>
                360° View Result
              </Typography>

              {/* Previously Generated 360 Videos */}
              {loadingConceptVideos && (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                  <CircularProgress size={24} />
                  <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                    Loading previous 360° views...
                  </Typography>
                </Box>
              )}

              {!loadingConceptVideos && videos360.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
                    Previously Generated ({videos360.length})
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {videos360.map((videoUrl, index) => (
                      <Box key={index}>
                        <Box sx={{ mb: 1, bgcolor: 'black', borderRadius: 1 }}>
                          <video
                            src={videoUrl}
                            controls
                            loop
                            style={{ width: '100%', display: 'block' }}
                          />
                        </Box>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<DownloadIcon />}
                          onClick={() => handleDownloadVideo(videoUrl)}
                          fullWidth
                        >
                          Download 360° Video {index + 1}
                        </Button>
                      </Box>
                    ))}
                  </Box>
                  <Divider sx={{ my: 3 }} />
                </Box>
              )}

              {generatedVideoUrl ? (
                <>
                  <Box sx={{ mb: 2, bgcolor: 'black', borderRadius: 1 }}>
                    <video
                      src={generatedVideoUrl}
                      controls
                      autoPlay
                      loop
                      style={{ width: '100%', display: 'block' }}
                    />
                  </Box>

                  {/* Video Metadata */}
                  {videoResponse && videoResponse.videos && videoResponse.videos[0] && (
                    <Box sx={{
                      mb: 2,
                      p: 2,
                      bgcolor: 'rgba(0, 0, 0, 0.6)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: 1
                    }}>
                      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.light' }}>
                        Video Details
                      </Typography>
                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Model:</Typography>
                          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.95)' }}>{videoResponse.model}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Provider:</Typography>
                          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.95)' }}>{videoResponse.provider}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Resolution:</Typography>
                          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.95)' }}>{videoResponse.videos[0].resolution}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Audio:</Typography>
                          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.95)' }}>
                            {videoResponse.videos[0].hasAudio ? '✓ Yes' : '✗ No'}
                          </Typography>
                        </Grid>
                        {videoResponse.videos[0].duration > 0 && (
                          <Grid item xs={6}>
                            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Duration:</Typography>
                            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.95)' }}>{videoResponse.videos[0].duration}s</Typography>
                          </Grid>
                        )}
                        <Grid item xs={6}>
                          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Format:</Typography>
                          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.95)' }}>{videoResponse.videos[0].contentType}</Typography>
                        </Grid>
                      </Grid>
                    </Box>
                  )}

                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="contained"
                      startIcon={<DownloadIcon />}
                      onClick={() => handleDownloadVideo(generatedVideoUrl)}
                      fullWidth
                    >
                      Download Video
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setGeneratedVideoUrl(null);
                        setVideoResponse(null);
                      }}
                    >
                      Clear
                    </Button>
                  </Box>
                  <Alert severity="success" sx={{ mt: 2 }}>
                    <Typography variant="caption">
                      Perfect for e-commerce product pages, presentations, and social media!
                    </Typography>
                  </Alert>
                </>
              ) : (
                <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
                  <View360Icon sx={{ fontSize: 64, opacity: 0.3 }} />
                  <Typography>No 360° view generated yet</Typography>
                  <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                    Select an image and generate to see a rotating turntable view
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={activeTab} index={6}>
        {/* Virtual Try-On Tab */}
        <Grid container spacing={{ xs: 2, sm: 3 }}>
          {/* Setup Panel (Left) */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <VirtualTryOnIcon />
                Virtual Try-On Setup
              </Typography>

              <Alert severity="info" sx={{ mb: 2 }}>
                Upload model and garment images, or select from generated designs below
              </Alert>

              {/* Model Selection */}
              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel>Try-On Model</InputLabel>
                <Select
                  value={tryOnModel}
                  onChange={(e) => setTryOnModel(e.target.value as VirtualTryOnModel)}
                  label="Try-On Model"
                >
                  <MenuItem value="fashn-v16">
                    {VIRTUAL_TRYON_MODELS['fashn-v16'].displayName}
                  </MenuItem>
                  <MenuItem value="fashn-v15">
                    {VIRTUAL_TRYON_MODELS['fashn-v15'].displayName}
                  </MenuItem>
                  <MenuItem value="idm-vton">
                    {VIRTUAL_TRYON_MODELS['idm-vton'].displayName}
                  </MenuItem>
                  <MenuItem value="cat-vton">
                    {VIRTUAL_TRYON_MODELS['cat-vton'].displayName}
                  </MenuItem>
                  <MenuItem value="leffa">
                    {VIRTUAL_TRYON_MODELS['leffa'].displayName}
                  </MenuItem>
                  <MenuItem value="kling-kolors-v15">
                    {VIRTUAL_TRYON_MODELS['kling-kolors-v15'].displayName}
                  </MenuItem>
                </Select>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  {VIRTUAL_TRYON_MODELS[tryOnModel].description}
                  <br />
                  <strong>Resolution:</strong> {VIRTUAL_TRYON_MODELS[tryOnModel].resolution} |
                  <strong> Pricing:</strong> {VIRTUAL_TRYON_MODELS[tryOnModel].pricing}
                </Typography>
              </FormControl>

              {/* Human/Model Image Section */}
              <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                🧍 Model Image
              </Typography>
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="human-image-upload"
                type="file"
                onChange={handleHumanImageUpload}
              />
              <label htmlFor="human-image-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<UploadIcon />}
                  fullWidth
                  sx={{ mb: 1 }}
                >
                  Upload Model Image
                </Button>
              </label>

              {humanImagePreview && (
                <Box sx={{ mb: 2, textAlign: 'center', position: 'relative', bgcolor: 'grey.100', borderRadius: 1, p: 1 }}>
                  <img
                    src={humanImagePreview}
                    alt="Model preview"
                    style={{ maxWidth: '100%', maxHeight: 200, objectFit: 'contain', borderRadius: 8 }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => {
                      setHumanImagePreview(null);
                      setHumanImageFile(null);
                    }}
                    sx={{ position: 'absolute', top: 8, right: 8, bgcolor: 'background.paper' }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              )}

              {generatedImages.length > 0 && (
                <>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    Or select from your generated designs:
                  </Typography>
                  <Grid container spacing={1} sx={{ mb: 3 }}>
                    {generatedImages.slice(0, 4).map((url, index) => (
                      <Grid item xs={3} key={index}>
                        <Box
                          onClick={() => selectGeneratedImageForTryOn(url, 'human')}
                          sx={{
                            cursor: 'pointer',
                            border: humanImagePreview === url ? 3 : 1,
                            borderColor: humanImagePreview === url ? 'primary.main' : 'grey.300',
                            borderRadius: 1,
                            overflow: 'hidden',
                            '&:hover': { borderColor: 'primary.light', opacity: 0.8 }
                          }}
                        >
                          <img src={url} alt={`Design ${index + 1}`} style={{ width: '100%', display: 'block' }} />
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </>
              )}

              {/* Garment Image Section */}
              <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 3 }}>
                👕 Garment Image
              </Typography>
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="garment-image-upload"
                type="file"
                onChange={handleGarmentImageUpload}
              />
              <label htmlFor="garment-image-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<UploadIcon />}
                  fullWidth
                  sx={{ mb: 1 }}
                >
                  Upload Garment Image
                </Button>
              </label>

              {garmentImagePreview && (
                <Box sx={{ mb: 2, textAlign: 'center', position: 'relative', bgcolor: 'grey.100', borderRadius: 1, p: 1 }}>
                  <img
                    src={garmentImagePreview}
                    alt="Garment preview"
                    style={{ maxWidth: '100%', maxHeight: 200, objectFit: 'contain', borderRadius: 8 }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => {
                      setGarmentImagePreview(null);
                      setGarmentImageFile(null);
                    }}
                    sx={{ position: 'absolute', top: 8, right: 8, bgcolor: 'background.paper' }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              )}

              {generatedImages.length > 0 && (
                <>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    Or select from your generated designs:
                  </Typography>
                  <Grid container spacing={1} sx={{ mb: 3 }}>
                    {generatedImages.slice(0, 4).map((url, index) => (
                      <Grid item xs={3} key={index}>
                        <Box
                          onClick={() => selectGeneratedImageForTryOn(url, 'garment')}
                          sx={{
                            cursor: 'pointer',
                            border: garmentImagePreview === url ? 3 : 1,
                            borderColor: garmentImagePreview === url ? 'primary.main' : 'grey.300',
                            borderRadius: 1,
                            overflow: 'hidden',
                            '&:hover': { borderColor: 'primary.light', opacity: 0.8 }
                          }}
                        >
                          <img src={url} alt={`Design ${index + 1}`} style={{ width: '100%', display: 'block' }} />
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </>
              )}

              {/* Model-Specific Settings */}

              {/* IDM-VTON Settings */}
              {tryOnModel === 'idm-vton' && (
                <>
                  <TextField
                    fullWidth
                    label="Garment Description"
                    value={garmentDescription}
                    onChange={(e) => setGarmentDescription(e.target.value)}
                    placeholder="e.g., Short Sleeve Round Neck T-shirts"
                    helperText="Required: Describe the garment type and style"
                    required
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    type="number"
                    label="Inference Steps"
                    value={tryOnInferenceSteps}
                    onChange={(e) => setTryOnInferenceSteps(parseInt(e.target.value) || 30)}
                    size="small"
                    inputProps={{ min: 1, max: 50 }}
                    helperText="Number of inference steps (1-50, default: 30)"
                    sx={{ mb: 2 }}
                  />
                </>
              )}

              {/* FASHN V1.5 & V1.6 Settings */}
              {(tryOnModel === 'fashn-v16' || tryOnModel === 'fashn-v15') && (
                <>
                  <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ mb: 2 }}>
                    <Grid item xs={6}>
                      <FormControl fullWidth>
                        <InputLabel>Category</InputLabel>
                        <Select
                          value={tryOnCategory}
                          onChange={(e) => setTryOnCategory(e.target.value as GarmentCategory)}
                          label="Category"
                          size="small"
                        >
                          <MenuItem value="auto">Auto-Detect</MenuItem>
                          <MenuItem value="tops">Tops</MenuItem>
                          <MenuItem value="bottoms">Bottoms</MenuItem>
                          <MenuItem value="one-pieces">One-Pieces</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={6}>
                      <FormControl fullWidth>
                        <InputLabel>Quality</InputLabel>
                        <Select
                          value={tryOnMode}
                          onChange={(e) => setTryOnMode(e.target.value as TryOnMode)}
                          label="Quality"
                          size="small"
                        >
                          <MenuItem value="performance">Fast</MenuItem>
                          <MenuItem value="balanced">Balanced</MenuItem>
                          <MenuItem value="quality">Best</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>

                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Garment Photo Type</InputLabel>
                    <Select
                      value={garmentPhotoType}
                      onChange={(e) => setGarmentPhotoType(e.target.value as GarmentPhotoType)}
                      label="Garment Photo Type"
                      size="small"
                    >
                      <MenuItem value="auto">Auto-Detect</MenuItem>
                      <MenuItem value="model">On-Model Photo</MenuItem>
                      <MenuItem value="flat-lay">Flat-Lay Product</MenuItem>
                    </Select>
                  </FormControl>
                </>
              )}

              {/* CAT-VTON Settings */}
              {tryOnModel === 'cat-vton' && (
                <>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Garment Type</InputLabel>
                    <Select
                      value={catGarmentType}
                      onChange={(e) => setCatGarmentType(e.target.value as typeof catGarmentType)}
                      label="Garment Type"
                      size="small"
                    >
                      <MenuItem value="upper">Upper (Shirts, Jackets)</MenuItem>
                      <MenuItem value="lower">Lower (Pants, Skirts)</MenuItem>
                      <MenuItem value="overall">Overall (Full Outfits)</MenuItem>
                      <MenuItem value="inner">Inner Wear</MenuItem>
                      <MenuItem value="outer">Outer Wear</MenuItem>
                    </Select>
                  </FormControl>

                  <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ mb: 2 }}>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Inference Steps"
                        value={tryOnInferenceSteps}
                        onChange={(e) => setTryOnInferenceSteps(parseInt(e.target.value) || 30)}
                        size="small"
                        inputProps={{ min: 1, max: 50 }}
                        helperText="Default: 30"
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Guidance Scale"
                        value={tryOnGuidanceScale}
                        onChange={(e) => setTryOnGuidanceScale(parseFloat(e.target.value) || 2.5)}
                        size="small"
                        inputProps={{ min: 0, max: 20, step: 0.1 }}
                        helperText="Default: 2.5"
                      />
                    </Grid>
                  </Grid>
                </>
              )}

              {/* Leffa Settings */}
              {tryOnModel === 'leffa' && (
                <>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Garment Type</InputLabel>
                    <Select
                      value={leffaGarmentType}
                      onChange={(e) => setLeffaGarmentType(e.target.value as typeof leffaGarmentType)}
                      label="Garment Type"
                      size="small"
                    >
                      <MenuItem value="upper_body">Upper Body</MenuItem>
                      <MenuItem value="lower_body">Lower Body</MenuItem>
                      <MenuItem value="dresses">Dresses</MenuItem>
                    </Select>
                  </FormControl>

                  <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ mb: 2 }}>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Inference Steps"
                        value={tryOnInferenceSteps}
                        onChange={(e) => setTryOnInferenceSteps(parseInt(e.target.value) || 30)}
                        size="small"
                        inputProps={{ min: 1, max: 50 }}
                        helperText="Default: 30"
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Guidance Scale"
                        value={tryOnGuidanceScale}
                        onChange={(e) => setTryOnGuidanceScale(parseFloat(e.target.value) || 2.5)}
                        size="small"
                        inputProps={{ min: 0, max: 20, step: 0.1 }}
                        helperText="Default: 2.5"
                      />
                    </Grid>
                  </Grid>

                  <FormControlLabel
                    control={
                      <Switch
                        checked={enableSafetyChecker}
                        onChange={(e) => setEnableSafetyChecker(e.target.checked)}
                      />
                    }
                    label="Enable Safety Checker (NSFW Detection)"
                    sx={{ mb: 2 }}
                  />
                </>
              )}

              {/* Kling Kolors V1.5 - No additional settings needed */}
              {tryOnModel === 'kling-kolors-v15' && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    This model uses auto-detection and requires no additional configuration.
                    Simply upload your images and generate!
                  </Typography>
                </Alert>
              )}

              {/* Seed Control */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="body2" sx={{ flexGrow: 1 }}>
                    Seed: {seed}
                  </Typography>
                  <Tooltip title="Generate random seed">
                    <IconButton
                      size="small"
                      onClick={() => setSeed(Math.floor(Math.random() * 2147483647))}
                    >
                      <RefreshIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
                <TextField
                  fullWidth
                  type="number"
                  value={seed}
                  onChange={(e) => setSeed(parseInt(e.target.value) || 0)}
                  size="small"
                  inputProps={{ min: 0, max: 2147483647 }}
                  helperText="For reproducible results"
                />
              </Box>

              {/* Generate Button */}
              <Button
                variant="contained"
                size="large"
                fullWidth
                onClick={handleVirtualTryOn}
                disabled={generatingTryOn || !humanImagePreview || !garmentImagePreview}
                startIcon={generatingTryOn ? <CircularProgress size={20} /> : <VirtualTryOnIcon />}
              >
                {generatingTryOn ? 'Generating Try-On...' : 'Generate Virtual Try-On'}
              </Button>

              {generatingTryOn && (
                <Box sx={{ mt: 2 }}>
                  <LinearProgress />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
                    Generating with {VIRTUAL_TRYON_MODELS[tryOnModel].displayName}...
                    <br />
                    This may take 30-60 seconds
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Results Panel (Right) */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1.0625rem', sm: '1.25rem' } }}>
                Try-On Results
              </Typography>

              {/* Previously Generated Try-On Images */}
              {loadingConceptTryOns && (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                  <CircularProgress size={24} />
                  <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                    Loading previous try-ons...
                  </Typography>
                </Box>
              )}

              {!loadingConceptTryOns && virtualTryOnImages.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
                    Previously Generated ({virtualTryOnImages.length})
                  </Typography>
                  <ImageList cols={1} gap={12}>
                    {virtualTryOnImages.map((imageUrl, index) => (
                      <ImageListItem key={index}>
                        <img
                          src={imageUrl}
                          alt={`Previous try-on ${index + 1}`}
                          loading="lazy"
                          style={{ borderRadius: 8, border: '1px solid #e0e0e0' }}
                        />
                        <ImageListItemBar
                          title={`Previous Result ${index + 1}`}
                          actionIcon={
                            <IconButton
                              sx={{ color: 'rgba(255, 255, 255, 0.9)' }}
                              onClick={() => handleDownloadImage(imageUrl, index)}
                              title="Download image"
                            >
                              <DownloadIcon />
                            </IconButton>
                          }
                        />
                      </ImageListItem>
                    ))}
                  </ImageList>
                  <Divider sx={{ my: 3 }} />
                </Box>
              )}

              {tryOnResults.length > 0 ? (
                <>
                  <ImageList cols={1} gap={16}>
                    {tryOnResults.map((url, index) => (
                      <ImageListItem key={index}>
                        <img src={url} alt={`Try-on result ${index + 1}`} loading="lazy" style={{ borderRadius: 8 }} />
                        <ImageListItemBar
                          title={`Result ${index + 1}`}
                          subtitle={`${VIRTUAL_TRYON_MODELS[tryOnModel].displayName} • ${VIRTUAL_TRYON_MODELS[tryOnModel].resolution}`}
                          actionIcon={
                            <IconButton
                              sx={{ color: 'rgba(255, 255, 255, 0.9)' }}
                              onClick={() => handleDownloadImage(url, index)}
                              title="Download result"
                            >
                              <DownloadIcon />
                            </IconButton>
                          }
                        />
                      </ImageListItem>
                    ))}
                  </ImageList>

                  {/* Segmentation Mask (IDM-VTON only) */}
                  {tryOnMask && tryOnModel === 'idm-vton' && (
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CheckIcon color="success" />
                        Segmentation Mask
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                        Shows detected garment regions for precise try-on
                      </Typography>
                      <img
                        src={tryOnMask}
                        alt="Segmentation mask"
                        style={{ width: '100%', borderRadius: 8, border: '1px solid #e0e0e0' }}
                      />
                    </Box>
                  )}

                  {/* NSFW Detection (Leffa only) */}
                  {tryOnModel === 'leffa' && hasNsfwConcepts !== undefined && (
                    <Alert severity={hasNsfwConcepts ? 'warning' : 'success'} sx={{ mt: 3 }}>
                      <Typography variant="body2">
                        <strong>Safety Check:</strong> {hasNsfwConcepts ? 'NSFW content detected' : 'Content is safe'}
                      </Typography>
                    </Alert>
                  )}

                  {/* Model Info */}
                  <Alert severity="success" sx={{ mt: 3 }}>
                    <Typography variant="body2">
                      <strong>Model:</strong> {VIRTUAL_TRYON_MODELS[tryOnModel].displayName}
                      <br />
                      <strong>Resolution:</strong> {VIRTUAL_TRYON_MODELS[tryOnModel].resolution}
                      <br />
                      <strong>Features:</strong> {VIRTUAL_TRYON_MODELS[tryOnModel].features.join(', ')}
                      {tryOnModel === 'fashn-v16' && (
                        <>
                          <br />
                          <strong>Cost:</strong> {VIRTUAL_TRYON_MODELS[tryOnModel].pricing}
                        </>
                      )}
                    </Typography>
                  </Alert>

                  <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                    <Button
                      variant="outlined"
                      startIcon={<RefreshIcon />}
                      onClick={() => {
                        setTryOnResults([]);
                        setTryOnMask(null);
                      }}
                      fullWidth
                    >
                      Clear Results
                    </Button>
                  </Box>
                </>
              ) : (
                <Box sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
                  <VirtualTryOnIcon sx={{ fontSize: 80, opacity: 0.2, mb: 2 }} />
                  <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1.0625rem', sm: '1.25rem' } }}>
                    No Try-On Results Yet
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 3 }}>
                    Upload a model image and a garment image to see the magic!
                  </Typography>
                  <Alert severity="info" sx={{ maxWidth: 400, mx: 'auto' }}>
                    <Typography variant="caption">
                      <strong>Tip:</strong> Use high-quality images for best results.
                      The model image should show the person clearly, and the garment
                      image should be well-lit with minimal background.
                    </Typography>
                  </Alert>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Floating Action Buttons */}
      <Box sx={{ position: 'fixed', bottom: 16, right: 16, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Tooltip title="Save Session" placement="left">
          <Fab color="primary" onClick={handleSaveSession}>
            <SaveIcon />
          </Fab>
        </Tooltip>
      </Box>
    </Container>
  );
};

export default FashionDesignStudio;
